import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
    
    // Restore layout from sessionStorage immediately after component initialization
    // This ensures consistent behavior on initial load and refresh
    // The layout restoration happens synchronously to prevent any visual flash
    this.#restoreLayout();
  }

  disconnectedCallback() {
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };

  /**
   * Restores the layout from sessionStorage on initialization
   * This method ensures the layout is set and radio buttons are synchronized.
   * The inline script in product-grid.liquid may have already set the layout attribute,
   * but we still need to ensure radio buttons are checked correctly.
   */
  #restoreLayout() {
    // Use requestAnimationFrame to ensure refs are fully updated
    requestAnimationFrame(() => {
      const { grid } = this.refs;
      if (!grid) {
        // If grid is not available yet, try again after a short delay
        setTimeout(() => {
          if (this.isConnected) {
            this.#restoreLayout();
          }
        }, 50);
        return;
      }

      const storedLayoutMobile = sessionStorage.getItem('product-grid-view-mobile') || 'default';
      const storedLayoutDesktop = sessionStorage.getItem('product-grid-view-desktop') || 'default';

      // No need to read the window width if no custom layouts were saved.
      if (storedLayoutMobile === 'default' && storedLayoutDesktop === 'default') return;

      const storedLayout = mediaQueryLarge.matches ? storedLayoutDesktop : storedLayoutMobile;
      if (storedLayout === 'default') return;

      // Ensure the layout attribute is set (may have been set by inline script)
      const currentLayout = grid.getAttribute('product-grid-view');
      if (currentLayout !== storedLayout) {
        grid.setAttribute('product-grid-view', storedLayout);
      }

      // Update the radio button inputs to match the stored layout
      // This is important for UI consistency
      const options = this.querySelectorAll(
        'input[type="radio"][name="grid"], input[type="radio"][name="grid-mobile"]'
      );
      for (const option of options) {
        if (option instanceof HTMLInputElement && option.value === storedLayout) {
          option.checked = true;
        }
      }
    });
  }
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
